import * as ActionTypes from '../../../constants';
import { listFiles, deleteFile } from '../../../firebase/storage';
import { incrementUserTotalSize } from '../../../firebase/auth';
import * as projects from '../../../firebase/projects';
import {
  findAssetFileIds,
  findParentId,
  removeFilesFromTree,
  storagePathFromUrl,
  matchAssetsToProjects
} from '../utils/assetReferences';
import { startLoader, stopLoader } from '../reducers/loading';
import { assetsActions } from '../reducers/assets';
import { runWithBusyToast } from '../utils/busyToast';
import { showToast, setToastText } from './toast';
import { deleteFile as removeEditorFile } from './files';

const { setAssets, deleteAsset, incrementAssetTotalSize } = assetsActions;

export { incrementAssetTotalSize };

/**
 * Adjust the user's cumulative media usage by `deltaBytes` (positive on upload/clone, negative on
 * delete) in BOTH Redux (so the quota gate + usage bar update in-session) and Firestore (so it
 * survives reload). This is the single accounting hook — keeps users/{uid}.totalSize from drifting
 * up-only.
 */
export function adjustTotalSize(deltaBytes) {
  return (dispatch) => {
    if (!deltaBytes) return;
    dispatch({
      type: ActionTypes.INCREMENT_USER_TOTAL_SIZE,
      value: deltaBytes
    });
    incrementUserTotalSize(deltaBytes).catch(() => {});
  };
}

export function getAssets() {
  return async (dispatch, getState) => {
    const uid = getState().user.id;
    if (!uid) return;
    dispatch(startLoader());
    try {
      const [{ assets: objects, totalSize }, projectList] = await Promise.all([
        listFiles(uid),
        projects.listOwnProjectsWithFiles()
      ]);
      // Upstream logic: an asset's display name + owning sketch come from the project file that
      // references it; objects no sketch references are hidden. Override the OPEN sketch's persisted
      // files with its live tree so a just-uploaded/renamed asset shows immediately (before save).
      const openId = getState().project?.id;
      const liveFiles = getState().files;
      const merged = projectList.map((p) =>
        p.id === openId && liveFiles ? { ...p, files: liveFiles } : p
      );
      const assets = matchAssetsToProjects(objects, merged);

      dispatch(setAssets({ assets, totalSize }));
      // totalSize sums ALL real Storage objects (matched or not) — reconcile the quota to it so the
      // gate can't drift from reality.
      const userTotal = getState().user.totalSize || 0;
      if (totalSize !== userTotal) {
        dispatch({
          type: ActionTypes.INCREMENT_USER_TOTAL_SIZE,
          value: totalSize - userTotal
        });
      }
      dispatch(stopLoader());
    } catch (error) {
      // Surface the failure instead of silently rendering the empty "No uploaded assets" state,
      // which is indistinguishable from a genuinely empty list.
      dispatch(stopLoader());
      dispatch(showToast(5500));
      dispatch(setToastText('Could not load your assets. Please try again.'));
    }
  };
}

/**
 * Scan all of the user's own sketches for files referencing `assetKey`. Returns the affected sketches
 * (each with the referencing file ids), or `null` if the scan fails. Used to warn before deleting an
 * asset and to drive the reference cleanup.
 */
export function getAssetUsage(assetKey) {
  return async () => {
    try {
      const all = await projects.listOwnProjectsWithFiles();
      return (all || [])
        .map((proj) => ({
          ...proj,
          refIds: findAssetFileIds(proj.files, assetKey)
        }))
        .filter((proj) => proj.refIds.length > 0);
    } catch (error) {
      return null;
    }
  };
}

/**
 * When a media file is deleted from a sketch's tree, delete its underlying Storage asset too — but
 * ONLY if no other sketch (and no remaining file in the current sketch) still references it. If the
 * usage scan fails, we keep the asset (deleting a possibly-shared object is worse than an orphan).
 * `url` is the file node's Firebase download URL.
 */
export function maybeDeleteOrphanedAsset(url) {
  return async (dispatch, getState) => {
    const assetKey = storagePathFromUrl(url);
    if (!assetKey) return;
    // Still used by another file in the current (live) sketch after this node was removed?
    if (findAssetFileIds(getState().files, assetKey).length > 0) return;

    const affected = await dispatch(getAssetUsage(assetKey));
    if (affected === null) return; // scan failed — don't risk deleting a maybe-shared asset
    const currentId = getState().project?.id;
    if (affected.some((proj) => proj.id !== currentId)) return; // used elsewhere → keep

    // Orphaned: delete the Storage object and reconcile the Assets list + quota.
    const asset = (getState().assets?.list || []).find(
      (a) => a.key === assetKey
    );
    try {
      await deleteFile(assetKey);
      dispatch(deleteAsset(assetKey));
      if (asset?.size) dispatch(adjustTotalSize(-asset.size));
    } catch (error) {
      // Best-effort: the sketch reference is already gone; a leftover object is reconciled later.
    }
  };
}

async function deleteAssetWork(assetKey, affected, dispatch, getState) {
  const asset = (getState().assets?.list || []).find((a) => a.key === assetKey);
  try {
    await deleteFile(assetKey);
    dispatch(deleteAsset(assetKey));
    // Reconcile the cumulative quota DOWN (Redux + Firestore) so deletes free up real space —
    // otherwise users/{uid}.totalSize only ever grows and the user eventually can't upload.
    if (asset?.size) {
      dispatch(adjustTotalSize(-asset.size));
    }
  } catch (error) {
    dispatch(showToast('Could not delete the file. Please try again.', 5500));
    return;
  }

  // The Storage object is gone — now pull its dangling references out of the user's sketches so they
  // don't keep a broken file in the tree. Re-scan if the caller didn't pass a pre-scanned list.
  let list = affected;
  if (list === undefined) {
    list = await dispatch(getAssetUsage(assetKey));
  }
  if (!list || list.length === 0) return;

  const openId = getState().project?.id;
  const openProj = list.find((proj) => proj.id === openId);
  const others = list.filter((proj) => proj.id !== openId);

  if (openProj) {
    // Currently-open sketch: remove from the LIVE file tree only, so the user's unsaved edits are
    // preserved and the removal is persisted by their next save (writing the last-saved files here
    // would clobber unsaved work and risk a stale-version conflict).
    const liveFiles = getState().files;
    const refIds =
      openProj.refIds || findAssetFileIds(openProj.files, assetKey);
    refIds.forEach((id) =>
      dispatch(removeEditorFile(id, findParentId(liveFiles, id)))
    );
  }

  try {
    await Promise.all(
      others.map((proj) => {
        const refIds = proj.refIds || findAssetFileIds(proj.files, assetKey);
        if (refIds.length === 0) return Promise.resolve();
        const newFiles = removeFilesFromTree(proj.files, refIds);
        return projects.updateProject(proj.id, { files: newFiles });
      })
    );
  } catch (error) {
    // One sketch failing to clean up must not abort the rest (the others already ran).
    dispatch(showToast('Could not update one of your sketches.', 5500));
  }
}

/**
 * Delete a media asset: the Storage object, then every dangling reference to it in the user's
 * sketches. The reference cleanup rewrites one project doc per affected sketch, so this is well
 * past the point where silence reads as a broken click.
 */
export function deleteAssetRequest(assetKey, affected = undefined) {
  return (dispatch, getState) =>
    dispatch(
      runWithBusyToast('Toast.DeletingFile', () =>
        deleteAssetWork(assetKey, affected, dispatch, getState)
      )
    );
}
